/**
 * Step 1 — Upload historic campaign data (CSV).
 * Themed rebuild of the old StepOne.tsx; same Papa.parse-based header/preview
 * extraction, just without antd Upload/Table.
 */

import React, { useEffect, useRef, useState } from "react";
import Papa from "papaparse";

export interface UploadStepData {
  dataFile: File;
  headers: string[];
  previewData: any[];
}

interface StepUploadProps {
  existingData?: UploadStepData | null;
  onNext: (data: UploadStepData) => void;
}

const StepUpload: React.FC<StepUploadProps> = ({ existingData, onNext }) => {
  const [file, setFile] = useState<File | null>(existingData?.dataFile ?? null);
  const [headers, setHeaders] = useState<string[]>(existingData?.headers ?? []);
  const [previewData, setPreviewData] = useState<any[]>(existingData?.previewData ?? []);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (existingData) {
      setFile(existingData.dataFile);
      setHeaders(existingData.headers);
      setPreviewData(existingData.previewData);
    }
  }, [existingData]);

  const parseFile = (f: File) => {
    setError(null);
    setIsParsing(true);
    Papa.parse(f, {
      header: true,
      preview: 10,
      complete: results => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          setHeaders(results.meta.fields);
          setPreviewData(results.data);
        } else {
          setError("Failed to parse CSV file. Please check the file format.");
        }
        setIsParsing(false);
      },
      error: () => {
        setError("Error parsing CSV file. Please check the file format.");
        setIsParsing(false);
      },
    });
  };

  const handleFileSelect = (f: File | undefined | null) => {
    if (!f) return;
    const isCsv = f.type === "text/csv" || f.name.toLowerCase().endsWith(".csv");
    if (!isCsv) {
      setError("You can only upload CSV files.");
      return;
    }
    setFile(f);
    parseFile(f);
  };

  const handleContinue = () => {
    if (!file) {
      setError("Please select a CSV file first.");
      return;
    }
    Papa.parse(file, {
      header: true,
      complete: results => {
        if (results.data && results.data.length > 0 && results.meta.fields) {
          onNext({
            dataFile: file,
            headers: results.meta.fields,
            previewData: previewData.length > 0 ? previewData : results.data.slice(0, 10),
          });
        } else {
          setError("Failed to parse CSV file. Please check the file format.");
        }
      },
      error: () => setError("Error parsing CSV file. Please check the file format."),
    });
  };

  return (
    <div style={{ maxWidth: 880 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--text-primary)", margin: "0 0 6px" }}>
        Upload Historic Data
      </h2>
      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "0 0 20px", lineHeight: 1.5 }}>
        Upload a CSV file containing your historic campaign data. This data will be used to optimize your bid strategy.
      </p>

      <div style={{
        padding: "14px 16px", borderRadius: "var(--radius)", marginBottom: 20,
        background: "var(--blue-subtle)", border: "1px solid var(--border-default)",
      }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--blue)", marginBottom: 4 }}>File Requirements</div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6 }}>
          • File must be in CSV format<br />
          • First row should contain column headers<br />
          • Include columns for customer identifiers and target variables<br />
          • Optional: demographic, geographic, and device data columns
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={e => handleFileSelect(e.target.files?.[0])}
      />

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleFileSelect(e.dataTransfer.files?.[0]); }}
        style={{
          padding: "28px 20px", borderRadius: "var(--radius)", textAlign: "center",
          border: "1.5px dashed var(--border-default)", background: "var(--bg-surface)",
          cursor: "pointer", marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
          {file ? file.name : "Click or drop a CSV file here"}
        </div>
        {!file && (
          <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 4 }}>.csv files only</div>
        )}
      </div>

      {isParsing && (
        <div style={{ textAlign: "center", padding: 24, color: "var(--text-tertiary)", fontSize: 13 }}>
          Parsing file…
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: 16, padding: "10px 14px", borderRadius: "var(--radius)",
          background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
          color: "#ef4444", fontSize: 12,
        }}>
          {error}
        </div>
      )}

      {!isParsing && headers.length > 0 && previewData.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <span style={{ color: "#10b981", fontSize: 14 }}>✓</span>
            <span style={{ fontSize: 12.5, fontWeight: 500, color: "#10b981" }}>File parsed successfully</span>
            <span style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginLeft: "auto" }}>
              Showing first {previewData.length} rows
            </span>
          </div>
          <div style={{ overflowX: "auto", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", maxHeight: 280 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {headers.map(h => (
                    <th key={h} style={{
                      position: "sticky", top: 0, textAlign: "left", padding: "8px 10px",
                      background: "var(--bg-hover)", color: "var(--text-secondary)",
                      borderBottom: "1px solid var(--border-default)", whiteSpace: "nowrap",
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, i) => (
                  <tr key={i}>
                    {headers.map(h => (
                      <td key={h} style={{
                        padding: "7px 10px", color: "var(--text-primary)",
                        borderBottom: "1px solid var(--border-subtle)", whiteSpace: "nowrap",
                      }}>
                        {String(row[h] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleContinue}
          disabled={!file || headers.length === 0}
          style={{
            padding: "9px 20px", borderRadius: "var(--radius)",
            background: !file || headers.length === 0 ? "var(--bg-hover)" : "var(--blue)",
            color: !file || headers.length === 0 ? "var(--text-tertiary)" : "#fff",
            border: "none", fontSize: 13, fontWeight: 500,
            cursor: !file || headers.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Continue with this file →
        </button>
      </div>
    </div>
  );
};

export default StepUpload;
